import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneInputWithCode } from "@/components/PhoneInputWithCode";
import { toast } from "sonner";
import axios from "axios";
import MonthYearPicker from "@/components/MonthYearPicker";
import {
  Briefcase, Plus, Send, Check, X, Mail, Clock,
  MapPin, Loader2, Building, User, Info
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY_FORM = {
  verifier_first_name: "",
  verifier_last_name: "",
  verifier_email: "",
  verifier_phone: "",
  verifier_phone_country_code: "+1|US",
  employer_name: "",
  employer_street: "",
  employer_city: "",
  employer_state: "",
  employer_zip: "",
  job_title: "",
  start_date: "",
  end_date: "",
  hours_worked: "",
};

const EmploymentHistorySections = ({ employmentHistory, setEmploymentHistory, onSectionComplete, isSubmitted = false, appId, token }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const verifications = employmentHistory.verifications || [];
  const totalRequestedHours = verifications.reduce((sum, v) => sum + (parseInt(v.hours_worked) || 0), 0);
  const totalConfirmedHours = verifications.filter(v => v.request_stage === "Completed").reduce((sum, v) => sum + (parseInt(v.hours_confirmed || v.hours_worked) || 0), 0);
  const completedCount = verifications.filter(v => v.request_stage === "Completed").length;
  const progressPct = Math.min((totalConfirmedHours / 2080) * 100, 100);

  const validateForm = () => {
    const e = {};
    if (!form.verifier_first_name.trim()) e.verifier_first_name = true;
    if (!form.verifier_last_name.trim()) e.verifier_last_name = true;
    if (!form.verifier_email.trim()) e.verifier_email = true;
    if (!form.employer_name.trim()) e.employer_name = true;
    if (!form.job_title.trim()) e.job_title = true;
    if (!form.start_date) e.start_date = true;
    setErrors(e);
    if (Object.keys(e).length) toast.error("Please fill in all required fields");
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
    const newV = {
      id: Date.now().toString(),
      ...form,
      hours_worked: parseInt(form.hours_worked) || 0,
      request_stage: "Pending",
      date_requested: now,
      date_confirmed: null,
      hours_confirmed: 0,
    };
    setEmploymentHistory({
      ...employmentHistory,
      verifications: [...verifications, newV]
    });
    setForm({ ...EMPTY_FORM });
    setErrors({});
    setShowForm(false);
    toast.success("Employment verification added. Send the email to notify your verifier.");
  };

  const handleSendEmail = async (v) => {
    if (!appId || !token) return;
    setSendingId(v.id);
    try {
      const currentVerifications = employmentHistory.verifications || [];
      await axios.put(
        `${API}/applications/${appId}`,
        { employment_history: { ...employmentHistory, verifications: currentVerifications } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const response = await axios.post(
        `${API}/applications/${appId}/employment/${v.id}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      setEmploymentHistory(prev => ({
        ...prev,
        verifications: (prev.verifications || []).map(ver =>
          ver.id === v.id ? { ...ver, request_stage: "Sent", token: data.token } : ver
        )
      }));
      const params = new URLSearchParams({
        verifierName: `${v.verifier_first_name} ${v.verifier_last_name}`,
        applicantName: data.applicant_name,
        program: data.program,
        portalToken: data.token,
        verifierEmail: v.verifier_email,
      });
      window.open(
        `${window.location.origin}/mock-employment-email?${params.toString()}`,
        "_blank"
      );
      toast.success("Employment verification request sent!");
    } catch (err) {
      console.error("Send employment verification error:", err);
      toast.error("Failed to send verification request");
    } finally {
      setSendingId(null);
    }
  };

  const handleRemove = (id) => {
    setEmploymentHistory({
      ...employmentHistory,
      verifications: verifications.filter(v => v.id !== id)
    });
  };

  const getStatusConfig = (stage) => {
    switch (stage) {
      case "Completed":
        return { color: "#739600", bg: "bg-[#739600]/15", border: "border-[#739600]/30", icon: Check, label: "Completed" };
      case "Sent":
        return { color: "#00677F", bg: "bg-[#00677F]/15", border: "border-[#00677F]/30", icon: Send, label: "Sent" };
      default:
        return { color: "#FF9800", bg: "bg-[#FF9800]/15", border: "border-[#FF9800]/30", icon: Clock, label: "Pending" };
    }
  };

  const inputCls = (name) => `h-11 bg-black/30 border-white/[0.08] text-white rounded-xl px-4 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600 ${errors[name] ? "border-red-400/50" : ""}`;
  const labelCls = "text-sm text-slate-300 flex items-center gap-1.5 mb-1.5";

  return (
    <div className={`space-y-8 ${isSubmitted ? 'pointer-events-none opacity-70' : ''}`} data-testid="subsection-employment-verification">

      {/* === Top Hero Card === */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/20 via-transparent to-[#FF9800]/10" />
        <div className="relative p-8">
          <div className="flex items-start justify-between gap-8">
            <div className="flex-1 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF6B35]/20 border border-[#FF6B35]/30">
                <Briefcase className="w-3.5 h-3.5 text-[#FF9800]" />
                <span className="text-[#FF9800] text-xs font-medium tracking-wide uppercase">Employment Verification</span>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed max-w-2xl">
                Applicants must submit proof of employment as an Occupational Therapy Assistant in a clinical setting for at least <strong className="text-white">2,080 hours</strong> (equivalent to 1 year full-time). Validation must come directly from your supervising clinician or HR representative.
              </p>
            </div>
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div className="relative w-24 h-24">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                  <circle
                    cx="50" cy="50" r="40" fill="none"
                    stroke={totalConfirmedHours >= 2080 ? "#739600" : "#FF6B35"}
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${progressPct * 2.51} 251`}
                    className="transition-all duration-700"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-xl font-bold ${totalConfirmedHours >= 2080 ? "text-[#739600]" : "text-white"}`}>
                    {totalConfirmedHours.toLocaleString()}
                  </span>
                  <span className="text-slate-500 text-[10px] uppercase tracking-wider">hours</span>
                </div>
              </div>
              <span className="text-slate-500 text-[10px] uppercase tracking-wider">/ 2,080</span>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t border-white/[0.05]">
            {[
              "Add Employer Verification",
              "Send Verification Email",
              "Employer Verifies Employment",
              "Status: Completed"
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2 flex-1">
                <div className="w-5 h-5 rounded-full bg-[#FF6B35]/30 border border-[#FF6B35]/40 flex items-center justify-center shrink-0">
                  <span className="text-[10px] text-[#FF9800] font-bold">{i + 1}</span>
                </div>
                <span className="text-slate-400 text-xs">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* === Add Button + Summary === */}
      <div className="flex items-center justify-between">
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#FF6B35] hover:bg-[#e55a25] text-white rounded-xl px-6 h-11 shadow-[0_0_20px_rgba(255,107,53,0.3)] hover:shadow-[0_0_30px_rgba(255,107,53,0.4)] transition-all"
          data-testid="add-employer-verification-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Employer Verification
        </Button>
        <div className="text-sm text-slate-400">
          Confirmed <span className="text-white font-semibold">{completedCount}</span> / <span className="text-slate-500">{verifications.length} verifications</span>
        </div>
      </div>

      {/* === Verification Cards === */}
      {verifications.length > 0 ? (
        <div className="space-y-3">
          {verifications.map((v) => {
            const status = getStatusConfig(v.request_stage);
            const StatusIcon = status.icon;
            return (
              <div
                key={v.id}
                className="group relative overflow-hidden rounded-2xl border border-white/[0.06] hover:border-white/[0.12] transition-all duration-300"
                data-testid={`emp-row-${v.id}`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative flex items-center gap-5 p-5">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${status.bg} border ${status.border}`}>
                    <StatusIcon className="w-4 h-4" style={{ color: status.color }} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-white font-medium text-sm">{v.employer_name}</h4>
                      <span className="px-2 py-0.5 rounded-full bg-[#7B68EE]/10 border border-[#7B68EE]/20 text-[#7B68EE] text-[10px] font-medium">
                        {v.job_title}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/20 text-[#FF9800] text-[10px] font-medium">
                        {v.hours_worked}h
                      </span>
                      {v.hours_confirmed > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-[#739600]/10 border border-[#739600]/20 text-[#739600] text-[10px] font-medium">
                          {v.hours_confirmed}h confirmed
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${status.bg} border ${status.border}`}
                        style={{ color: status.color }}
                        data-testid={`emp-status-${v.id}`}
                      >
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5">
                      <span className="flex items-center gap-1.5 text-slate-500 text-xs">
                        <User className="w-3 h-3" /> {v.verifier_first_name} {v.verifier_last_name}
                      </span>
                      <span className="flex items-center gap-1.5 text-slate-500 text-xs">
                        <Mail className="w-3 h-3" /> {v.verifier_email}
                      </span>
                      {v.employer_city && (
                        <span className="flex items-center gap-1.5 text-slate-500 text-xs">
                          <MapPin className="w-3 h-3" /> {v.employer_city}, {v.employer_state}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {v.request_stage === "Pending" && (
                      <Button
                        size="sm"
                        onClick={() => handleSendEmail(v)}
                        disabled={sendingId === v.id}
                        className="bg-[#FF6B35] hover:bg-[#e55a25] text-white rounded-full px-4 h-9 text-xs shadow-[0_0_12px_rgba(255,107,53,0.3)]"
                        data-testid={`emp-send-${v.id}`}
                      >
                        {sendingId === v.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Send Email
                      </Button>
                    )}
                    {v.request_stage === "Sent" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendEmail(v)}
                        disabled={sendingId === v.id}
                        className="border-[#FF6B35]/40 text-[#FF9800] hover:bg-[#FF6B35]/10 rounded-full px-4 h-9 text-xs"
                        data-testid={`emp-resend-${v.id}`}
                      >
                        {sendingId === v.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Mail className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Resend
                      </Button>
                    )}
                    <div className="text-right space-y-1">
                      <div className="text-slate-500 text-xs">
                        Requested <span className="text-slate-400">{v.date_requested}</span>
                      </div>
                      {v.date_confirmed && (
                        <div className="text-slate-500 text-xs">
                          Confirmed <span className="text-[#739600]">{new Date(v.date_confirmed).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {v.request_stage === "Pending" && (
                    <button
                      onClick={() => handleRemove(v.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-red-400 p-1"
                      data-testid={`emp-remove-${v.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-white/[0.08] p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-6 h-6 text-slate-600" />
          </div>
          <p className="text-slate-500 text-sm">No employment verifications yet</p>
          <p className="text-slate-600 text-xs mt-1">Click "Add Employer Verification" to get started</p>
        </div>
      )}

      {/* === Modal === */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="employment-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowForm(false); setErrors({}); }} />
          <div className="relative w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl shadow-black/40 animate-in fade-in zoom-in-95 duration-200">
            <div className="relative overflow-hidden px-6 py-5">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B35] to-[#FF9800]/80" />
              <div className="relative flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-white text-lg font-semibold">Add Employment Verification</h4>
                  <p className="text-white/60 text-xs">Enter your employer and verifier details</p>
                </div>
              </div>
            </div>

            <div className="bg-[#0f1419] border-x border-white/[0.08] p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* Verifier Info */}
              <div className="flex items-center gap-2 pb-2 border-b border-white/[0.05]">
                <User className="w-4 h-4 text-[#7B68EE]" />
                <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Verifier Information</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> First Name</Label>
                  <Input placeholder="First Name" value={form.verifier_first_name} onChange={(e) => setForm({ ...form, verifier_first_name: e.target.value })} className={inputCls("verifier_first_name")} data-testid="verifier-firstname" />
                </div>
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Last Name</Label>
                  <Input placeholder="Last Name" value={form.verifier_last_name} onChange={(e) => setForm({ ...form, verifier_last_name: e.target.value })} className={inputCls("verifier_last_name")} data-testid="verifier-lastname" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Email</Label>
                  <Input type="email" placeholder="email@employer.com" value={form.verifier_email} onChange={(e) => setForm({ ...form, verifier_email: e.target.value })} className={inputCls("verifier_email")} data-testid="verifier-email" />
                </div>
                <div>
                  <Label className={labelCls}>Phone</Label>
                  <PhoneInputWithCode
                    value={form.verifier_phone}
                    countryCode={form.verifier_phone_country_code || "+1|US"}
                    onPhoneChange={(val) => setForm({ ...form, verifier_phone: val })}
                    onCodeChange={(val) => setForm({ ...form, verifier_phone_country_code: val })}
                    testId="verifier-phone"
                    theme="dark"
                  />
                </div>
              </div>

              {/* Employer Info */}
              <div className="flex items-center gap-2 pb-2 border-b border-white/[0.05] pt-2">
                <Building className="w-4 h-4 text-[#FF9800]" />
                <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Employer Information</span>
              </div>
              <div>
                <Label className={labelCls}><span className="text-red-400">*</span> Organization / Practice Name</Label>
                <Input placeholder="Hospital / Clinic Name" value={form.employer_name} onChange={(e) => setForm({ ...form, employer_name: e.target.value })} className={inputCls("employer_name")} data-testid="employer-name" />
              </div>
              <div>
                <Label className={labelCls}>Street Address</Label>
                <Input placeholder="Street Address" value={form.employer_street} onChange={(e) => setForm({ ...form, employer_street: e.target.value })} className={inputCls("")} data-testid="employer-street" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className={labelCls}>ZIP Code</Label>
                  <Input
                    placeholder="ZIP"
                    value={form.employer_zip}
                    onChange={async (e) => {
                      const zip = e.target.value;
                      setForm(prev => ({ ...prev, employer_zip: zip }));
                      if (zip.length === 5) {
                        try {
                          const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
                          if (res.ok) {
                            const d = await res.json();
                            const p = d.places?.[0];
                            if (p) setForm(prev => ({ ...prev, employer_city: p['place name'], employer_state: p['state abbreviation'] }));
                          }
                        } catch (_) {}
                      }
                    }}
                    className={inputCls("")}
                    data-testid="employer-zip"
                  />
                </div>
                <div>
                  <Label className={labelCls}>City</Label>
                  <Input placeholder="City" value={form.employer_city} onChange={(e) => setForm({ ...form, employer_city: e.target.value })} className={inputCls("")} data-testid="employer-city" />
                </div>
                <div>
                  <Label className={labelCls}>State</Label>
                  <Input placeholder="State" value={form.employer_state} onChange={(e) => setForm({ ...form, employer_state: e.target.value })} className={inputCls("")} data-testid="employer-state" />
                </div>
              </div>
              <div>
                <Label className={labelCls}><span className="text-red-400">*</span> Your Job Title</Label>
                <Input placeholder="e.g., Occupational Therapy Assistant" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className={inputCls("job_title")} data-testid="job-title" />
              </div>

              {/* Employment Details */}
              <div className="flex items-center gap-2 pb-2 border-b border-white/[0.05] pt-2">
                <Clock className="w-4 h-4 text-[#739600]" />
                <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Employment Details</span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <MonthYearPicker label="Start Date" required={true} value={form.start_date} onChange={(date) => setForm({ ...form, start_date: date })} testId="employment-start" theme="dark" />
                <MonthYearPicker label="End Date" value={form.end_date} onChange={(date) => setForm({ ...form, end_date: date })} testId="employment-end" theme="dark" />
                <div>
                  <Label className={labelCls}>Total Hours</Label>
                  <Input type="number" placeholder="e.g., 2080" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} className={inputCls("")} data-testid="hours-worked" />
                </div>
              </div>
            </div>

            <div className="bg-[#0f1419] border border-white/[0.08] border-t-0 rounded-b-2xl px-6 pb-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowForm(false); setErrors({}); setForm({ ...EMPTY_FORM }); }}
                className="border-white/20 text-white hover:bg-white/5 rounded-xl px-6"
                data-testid="emp-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-[#FF6B35] hover:bg-[#e55a25] text-white rounded-xl px-6 shadow-[0_0_15px_rgba(255,107,53,0.3)]"
                data-testid="submit-verification-btn"
              >
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmploymentHistorySections;
